import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Image, Pencil, Home, Upload, Trash2, Save, X, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableSection({ section, canEdit, onEdit, onMove, isFirst, isLast }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-xl border border-garden-200 overflow-hidden" data-testid={`section-${section.slug}`}>
      <div className="flex items-start gap-4 p-4">
        {canEdit && (
          <div className="flex flex-col items-center gap-1 shrink-0 mt-2">
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-garden-400 hover:text-garden-600 touch-none hidden sm:block" data-testid={`drag-handle-${section.slug}`} aria-label="Glisser pour reordonner">
              <GripVertical size={20} />
            </button>
            <button onClick={() => onMove(section.id, 'up')} disabled={isFirst} className="text-garden-500 hover:text-garden-800 disabled:opacity-30 disabled:cursor-not-allowed p-1" data-testid={`move-up-${section.slug}`} aria-label="Monter">
              <ArrowUp size={16} />
            </button>
            <button onClick={() => onMove(section.id, 'down')} disabled={isLast} className="text-garden-500 hover:text-garden-800 disabled:opacity-30 disabled:cursor-not-allowed p-1" data-testid={`move-down-${section.slug}`} aria-label="Descendre">
              <ArrowDown size={16} />
            </button>
          </div>
        )}
        <div className="shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-garden-50 border border-garden-100">
          {section.image_url ? (
            <img src={section.image_url} alt={section.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-garden-300"><Image size={24} /></div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading font-semibold text-garden-800">{section.title || section.slug}</h3>
            <span className="text-xs bg-garden-100 text-garden-600 px-2 py-0.5 rounded-full">#{section.order}</span>
          </div>
          <p className="text-sm text-charcoal/60 mt-1 line-clamp-2">{section.subtitle || 'Pas de sous-titre'}</p>
        </div>
        <div className="shrink-0">
          {canEdit && (
            <Button variant="ghost" size="sm" onClick={() => onEdit(section)} className="shrink-0" data-testid={`edit-section-${section.slug}`}>
              <Pencil size={14} className="mr-1" /> Modifier
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HomepageManagement() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('HOMEPAGE_SECTIONS', 'edit');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSection, setEditSection] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await adminApi.getHomepageSections();
      setSections(res.data || []);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openEdit = (section) => {
    setEditSection({ ...section, _uploadFile: null });
    setShowDialog(true);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await adminApi.uploadHomepageImage(fd);
      setEditSection(prev => ({ ...prev, image_url: res.data.url, _uploadFile: null }));
      toast.success('Image uploadée');
    } catch { toast.error('Erreur upload'); }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!editSection) return;
    setSaving(true);
    try {
      const { _uploadFile, ...data } = editSection;
      await adminApi.updateHomepageSection(editSection.id, data);
      toast.success('Section mise à jour');
      setShowDialog(false);
      setEditSection(null);
      load();
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  };

  const removeImage = () => {
    setEditSection(prev => ({ ...prev, image_url: '' }));
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const persistOrder = async (ordered) => {
    try {
      await adminApi.reorderHomepageSections(ordered.map(s => ({ id: s.id, order: s.order })));
      toast.success('Ordre mis à jour');
    } catch {
      toast.error('Erreur lors du réordonnancement');
      load();
    }
  };

  const handleMove = async (id, direction) => {
    const idx = sections.findIndex(s => s.id === id);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= sections.length) return;
    const reordered = [...sections];
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    const updated = reordered.map((s, i) => ({ ...s, order: i + 1 }));
    setSections(updated);
    await persistOrder(updated);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    
    const reordered = [...sections];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    
    const updated = reordered.map((s, i) => ({ ...s, order: i + 1 }));
    setSections(updated);
    await persistOrder(updated);
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center py-20"><div className="w-10 h-10 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-heading font-bold text-garden-800 mb-6" data-testid="homepage-title">
          <Home size={24} className="inline mr-2" /> Page d'accueil
        </h1>

        {canEdit && (
          <p className="text-sm text-charcoal/50 mb-4">Glissez-deposez les sections (poignée à gauche, PC) ou utilisez les flèches monter/descendre pour modifier leur ordre d'affichage sur la page d'accueil.</p>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-4">
              {sections.map((section, idx) => (
                <SortableSection
                  key={section.id}
                  section={section}
                  canEdit={canEdit}
                  onEdit={openEdit}
                  onMove={handleMove}
                  isFirst={idx === 0}
                  isLast={idx === sections.length - 1}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {/* Dialog édition */}
        <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setShowDialog(false); setEditSection(null); } }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier : {editSection?.title || editSection?.slug}</DialogTitle>
            </DialogHeader>
            {editSection && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Titre</Label>
                <Input value={editSection.title || ''} onChange={e => setEditSection({ ...editSection, title: e.target.value })} className="border-garden-200 rounded-xl" data-testid="edit-section-title" />
              </div>
              <div>
                <Label className="text-sm">Sous-titre</Label>
                <Input value={editSection.subtitle || ''} onChange={e => setEditSection({ ...editSection, subtitle: e.target.value })} className="border-garden-200 rounded-xl" data-testid="edit-section-subtitle" />
              </div>
              <div>
                <Label className="text-sm">Contenu</Label>
                <Textarea value={editSection.content || ''} onChange={e => setEditSection({ ...editSection, content: e.target.value })} className="border-garden-200 rounded-xl" rows={3} data-testid="edit-section-content" />
              </div>

              {/* Image management */}
              <div className="border border-garden-200 rounded-xl p-4 space-y-3">
                <Label className="text-sm font-medium">Image</Label>
                
                {editSection.image_url ? (
                  <div className="relative">
                    <img src={editSection.image_url} alt="Aperçu" className="w-full max-h-40 object-contain rounded-lg border" />
                    <button onClick={removeImage} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600" data-testid="remove-section-image">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-24 bg-garden-50 rounded-lg border-2 border-dashed border-garden-200 flex items-center justify-center text-sm text-charcoal/40">
                    Aucune image
                  </div>
                )}

                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs text-charcoal/50">URL de l'image</Label>
                    <Input value={editSection.image_url || ''} onChange={e => setEditSection({ ...editSection, image_url: e.target.value })} className="border-garden-200 rounded-xl text-sm" placeholder="https://..." data-testid="edit-section-image-url" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-charcoal/50">Ou uploader une image</Label>
                  <div className="flex gap-2 items-center mt-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file) handleUpload(file);
                      }}
                      className="text-sm flex-1"
                      data-testid="upload-section-image"
                    />
                    {uploading && <span className="text-xs text-garden-600 animate-pulse">Upload...</span>}
                  </div>
                  <p className="text-[10px] text-charcoal/40 mt-1">L'image uploadée remplace l'URL précédente.</p>
                </div>
              </div>

              {editSection.cta_text !== undefined && (
                <div>
                  <Label className="text-sm">Texte du bouton</Label>
                  <Input value={editSection.cta_text || ''} onChange={e => setEditSection({ ...editSection, cta_text: e.target.value })} className="border-garden-200 rounded-xl" />
                </div>
              )}
              {editSection.cta_link !== undefined && (
                <div>
                  <Label className="text-sm">Lien du bouton</Label>
                  <Input value={editSection.cta_link || ''} onChange={e => setEditSection({ ...editSection, cta_link: e.target.value })} className="border-garden-200 rounded-xl" />
                </div>
              )}
            </div>
            )}
            
            {/* Boutons toujours visibles */}
            <DialogFooter className="pt-4 border-t border-garden-100 mt-4">
              <Button variant="outline" onClick={() => { setShowDialog(false); setEditSection(null); }} className="rounded-full" data-testid="cancel-section-edit">
                <X size={14} className="mr-1" /> Annuler
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="save-section-edit">
                <Save size={14} className="mr-1" /> {saving ? 'Sauvegarde...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
